import { Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Menu, Search, Shield, ShoppingBag, UserRound } from 'lucide-react'
import { useCart } from '#bv/lib/cart'
import { getPublicBanner } from '#bv/lib/adminApi'
import type { BannerItem } from '#bv/lib/adminTypes'
import { FeatureIcon, type FeatureIconName } from '#bv/components/FeatureIcons'
import { CurrencySelector } from '#bv/components/CurrencySelector'
import { tryGetSupabaseClient } from '#bv/lib/supabaseClient'
import { navigateToShopSearch } from '#bv/lib/shopSearch'

const FALLBACK_MARQUEE: BannerItem[] = [
  { icon: 'zap', text: 'INSTANT DELIVERY ON ALL ORDERS' },
  { icon: 'lock', text: 'SECURE CHECKOUT — SSL ENCRYPTED' },
  { icon: 'gamepad', text: 'FORTNITE & CLASH ACCOUNTS — FRESH STOCK DAILY' },
  { icon: 'gem', text: 'USE CODE PULSE10 FOR 10% OFF FIRST ORDER' },
  { icon: 'check', text: 'COMMUNITY ON DISCORD' },
]

function MarqueeLine({ text }: { text: string }): ReactNode {
  const marker = 'PULSE10'
  const idx = text.indexOf(marker)
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <strong className="text-[#00bfff]">{marker}</strong>
      {text.slice(idx + marker.length)}
    </>
  )
}

export function NewHeader() {
  const navigate = useNavigate()
  const { count } = useCart()
  const [q, setQ] = useState('')
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const mobileInputRef = useRef<HTMLInputElement>(null)
  const [banner, setBanner] = useState<{ enabled: boolean; items: BannerItem[] } | null>(null)
  const [bannerReady, setBannerReady] = useState(false)
  const [customerEmail, setCustomerEmail] = useState<string | null>(null)

  const primaryLinks = useMemo(
    () => [
      { to: '/', label: 'Home' },
      { to: '/shop', label: 'Pulse Shop' },
      { to: '/donation', label: 'Donation' },
      { to: '/contact', label: 'Contact' },
    ],
    [],
  )

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const raw = new FormData(e.currentTarget).get('q')
      const query = typeof raw === 'string' ? raw.trim() : q.trim()
      if (!query) return
      navigateToShopSearch(navigate, query)
      setQ('')
      setMobileSearchOpen(false)
      setMobileMenuOpen(false)
    },
    [navigate, q],
  )

  const onNavClick = useCallback(() => {
    setMobileMenuOpen(false)
    setMobileSearchOpen(false)
  }, [])

  useEffect(() => {
    getPublicBanner()
      .then((value) => setBanner(value))
      .catch(() => setBanner({ enabled: true, items: FALLBACK_MARQUEE }))
      .finally(() => setBannerReady(true))
  }, [])

  useEffect(() => {
    const supabase = tryGetSupabaseClient()
    if (!supabase) {
      setCustomerEmail(null)
      return
    }
    let cancelled = false

    void (async () => {
      try {
        const { data } = await supabase.auth.getSession()
        const em = data.session?.user?.email ?? null
        if (!cancelled) setCustomerEmail(em)
      } catch {
        if (!cancelled) setCustomerEmail(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!mobileSearchOpen) return
    const t = window.setTimeout(() => mobileInputRef.current?.focus(), 20)
    return () => window.clearTimeout(t)
  }, [mobileSearchOpen])

  useEffect(() => {
    if (!mobileSearchOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSearchOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [mobileSearchOpen])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [mobileMenuOpen])

  const showMarquee = bannerReady && banner?.enabled !== false
  const rows = banner && banner.items.length > 0 ? banner.items : FALLBACK_MARQUEE

  return (
    <header className="nv-header">
      {showMarquee ? (
        <div
          className="nv-announce"
          style={{
            background: 'linear-gradient(90deg, rgba(8,6,10,0.92) 0%, rgba(20,8,24,0.95) 50%, rgba(8,6,10,0.92) 100%)',
            borderBottom: '1px solid rgba(0, 191, 255, 0.26)',
            height: '32px',
            overflow: 'hidden',
          }}
        >
          <div className="flex h-full items-center">
            <div className="marquee-inner flex items-center gap-16 whitespace-nowrap px-8" style={{ width: 'max-content' }}>
              {[...Array(2)].map((_, i) => (
                <span key={i} className="flex items-center gap-16 text-xs font-semibold" style={{ color: '#f0f6ff' }}>
                  {rows.map((row, j) => (
                    <span key={`${row.icon}-${j}-${i}`} className="inline-flex items-center gap-2">
                      <FeatureIcon name={row.icon as FeatureIconName} size={16} className="shrink-0" />
                      <span>
                        <MarqueeLine text={row.text} />
                      </span>
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="nv-nav">
        <div className="nv-nav-inner">
          <Link to="/" className="nv-brand" aria-label="PulseDigital home">
            <span className="nv-brand-mark" aria-hidden>
              <img className="nv-brand-img" src="/pulsedigital-logo-mark.png" alt="" />
            </span>
            <span className="nv-brand-word">PulseDigital</span>
          </Link>

          <nav className="nv-links">
            {primaryLinks.map((l) => (
              <Link key={l.to} to={l.to} className="nv-link" activeProps={{ className: 'nv-link nv-link-active' }}>
                {l.label}
              </Link>
            ))}

          </nav>

          <div className="nv-actions">
            <form className="nv-search" onSubmit={onSubmit}>
              <Search size={16} className="nv-search-ic" aria-hidden />
              <input
                name="q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search Pulse Shop…"
                className="nv-search-input"
              />
            </form>

            <button
              type="button"
              className="nv-search-btn"
              aria-label="Search"
              aria-haspopup="dialog"
              aria-expanded={mobileSearchOpen}
              onClick={() => setMobileSearchOpen(true)}
            >
              <Search size={18} />
            </button>

            <button
              type="button"
              className="nv-menu-btn"
              aria-label="Menu"
              aria-haspopup="dialog"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={18} />
            </button>

            <Link to="/shop" className="nv-pill" aria-label="Browse Pulse Shop">
              Pulse Shop
            </Link>

            <CurrencySelector className="nv-currency max-[719px]:hidden" compact />

            <Link
              to={customerEmail ? '/customer' : '/customer/login'}
              search={customerEmail ? undefined : { next: '/customer' }}
              className="nv-action-ic"
              title={customerEmail ? 'Customer dashboard' : 'Sign in'}
              aria-label={customerEmail ? 'Customer dashboard' : 'Sign in'}
            >
              <UserRound size={18} strokeWidth={2.2} />
            </Link>

            <Link to="/admin/login" className="nv-action-ic nv-action-admin" title="Admin login" aria-label="Admin login">
              <Shield size={18} strokeWidth={2} />
            </Link>

            <Link to="/cart" className="nv-cart" aria-label="Cart">
              <ShoppingBag size={18} />
              {count > 0 ? <span className="nv-cart-badge">{count}</span> : null}
            </Link>
          </div>
        </div>
      </div>

      {mobileMenuOpen && typeof document !== 'undefined'
        ? createPortal(
            <div className="nv-menu-drawer" role="dialog" aria-modal="true" aria-label="Menu">
              <button type="button" className="nv-menu-drawer-backdrop" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)} />
              <div className="nv-menu-drawer-panel" onClick={(e) => e.stopPropagation()}>
                <div className="nv-menu-drawer-head">
                  <div className="nv-menu-drawer-title">PulseDigital</div>
                  <button type="button" className="nv-menu-drawer-close" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
                    ×
                  </button>
                </div>

                <div className="nv-menu-sections" aria-label="Mobile navigation">
                  <div className="nv-menu-section">
                    <div className="nv-menu-section-title">Store</div>
                    <div className="nv-menu-items">
                      <Link to="/" className="nv-menu-item" activeProps={{ className: 'nv-menu-item nv-menu-item-active' }} onClick={onNavClick}>
                        Home
                      </Link>
                      <Link to="/shop" className="nv-menu-item" activeProps={{ className: 'nv-menu-item nv-menu-item-active' }} onClick={onNavClick}>
                        Pulse Shop
                      </Link>
                      <Link to="/donation" className="nv-menu-item" activeProps={{ className: 'nv-menu-item nv-menu-item-active' }} onClick={onNavClick}>
                        Donation
                      </Link>
                      <Link to="/cart" className="nv-menu-item" onClick={onNavClick}>
                        Cart
                      </Link>
                    </div>
                  </div>

                  <div className="nv-menu-section">
                    <div className="nv-menu-section-title">Support</div>
                    <div className="nv-menu-items">
                      <Link to="/contact" className="nv-menu-item" activeProps={{ className: 'nv-menu-item nv-menu-item-active' }} onClick={onNavClick}>
                        Contact
                      </Link>
                      <Link to="/faq" className="nv-menu-item" activeProps={{ className: 'nv-menu-item nv-menu-item-active' }} onClick={onNavClick}>
                        FAQ
                      </Link>
                    </div>
                  </div>

                  <div className="nv-menu-section">
                    <div className="nv-menu-section-title">Legal</div>
                    <div className="nv-menu-items">
                      <Link to="/terms" className="nv-menu-item" activeProps={{ className: 'nv-menu-item nv-menu-item-active' }} onClick={onNavClick}>
                        Terms
                      </Link>
                      <Link to="/refund" className="nv-menu-item" activeProps={{ className: 'nv-menu-item nv-menu-item-active' }} onClick={onNavClick}>
                        Refunds
                      </Link>
                    </div>
                  </div>

                  <div className="nv-menu-section">
                    <div className="nv-menu-section-title">Preferences</div>
                    <div className="nv-menu-currency">
                      <CurrencySelector compact />
                    </div>
                  </div>

                  <div className="nv-menu-section">
                    <div className="nv-menu-section-title">Account</div>
                    <div className="nv-menu-items">
                      <Link
                        to={customerEmail ? '/customer' : '/customer/login'}
                        search={customerEmail ? undefined : { next: '/customer' }}
                        className="nv-menu-item"
                        activeProps={{ className: 'nv-menu-item nv-menu-item-active' }}
                        onClick={onNavClick}
                      >
                        {customerEmail ? 'Dashboard' : 'Sign in'}
                      </Link>
                      <Link
                        to="/admin/login"
                        className="nv-menu-item"
                        activeProps={{ className: 'nv-menu-item nv-menu-item-active' }}
                        onClick={onNavClick}
                      >
                        Admin login
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="nv-menu-drawer-foot">Payments secured by MoneyMotion · Discord-first support</div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {mobileSearchOpen && typeof document !== 'undefined'
        ? createPortal(
            <div className="nv-search-sheet" role="dialog" aria-modal="true" aria-label="Search Pulse Shop">
              <button type="button" className="nv-search-sheet-backdrop" aria-label="Close search" onClick={() => setMobileSearchOpen(false)} />
              <div className="nv-search-sheet-panel" onClick={(e) => e.stopPropagation()}>
                <div className="nv-search-sheet-head">
                  <div className="nv-search-sheet-title">Search</div>
                  <button type="button" className="nv-search-sheet-close" aria-label="Close search" onClick={() => setMobileSearchOpen(false)}>
                    ×
                  </button>
                </div>
                <form className="nv-search-sheet-form" onSubmit={onSubmit}>
                  <Search size={16} className="nv-search-sheet-ic" aria-hidden />
                  <input
                    ref={mobileInputRef}
                    name="q"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search products, categories…"
                    className="nv-search-sheet-input"
                  />
                  <button type="submit" className="nv-search-sheet-go" disabled={!q.trim()}>
                    Go
                  </button>
                </form>
                <div className="nv-search-sheet-foot">Search takes you to Pulse Shop results.</div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </header>
  )
}

